/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 03.06.25
 *
 */

import {
	FundingSourceEnum,
	FundingSourceNameMap
} from "@tl/core/modules/commission/model/fundingSources/FundingSourceEnum.js";
import BaseIdObject from "@shared/BaseIdObject";
import dayjs from "dayjs";
import {timeNow} from "@/utils/helpers";
import {
	ASF_FINANCIAL_YEAR_START_MS_DEFAULT,
	FINANCIAL_YEAR_START_MS_DEFAULT
} from "@shared/Quote/ServiceType";
import {classToClass} from "@qwoach/class-transformer-wrapper";

export default class FundingSourceItem extends BaseIdObject {

	type: FundingSourceEnum = null;

	email: string = null;
	billingOrganization: string = null;
	billingEmail: string = null;

	fundsYearOne: string = null;
	fundsYearTwo: string = null;

	financialYearStartMonthDateMs: string = null; // date + month in ms.

	asfNumber: string = null;
	asfPurchaseOrderNumber: string = null;

	createdAt : number = null;


	constructor(options = {}) {
		super(options);

		this.type = options.type || FundingSourceEnum.ASF;
		this.email = options.email || null;
		this.billingOrganization = options.billingOrganization || null;
		this.billingEmail = options.billingEmail || null;
		this.fundsYearOne = options.fundsYearOne || null;
		this.fundsYearTwo = options.fundsYearTwo || null;

		this.asfNumber = options.asfNumber || null;
		this.asfPurchaseOrderNumber = options.asfPurchaseOrderNumber || null;

		this.createdAt = timeNow()

		if(this.type === FundingSourceEnum.ASF){
			this.financialYearStartMonthDateMs = ASF_FINANCIAL_YEAR_START_MS_DEFAULT
		}

	}

	getName() {
		return FundingSourceNameMap[this.type] || "Funding Source";
	}

	getFinancialYearFundingTotal(dateMs = timeNow()) {

		let expanded = this.expand();

		for(let fundingSource of expanded) {
			if(!fundingSource.hasFinancialYear() || fundingSource.isFinancialYearOne(dateMs)) {
				return fundingSource.fundsYearOne;
			}
		}
		return 0
	}

	isFinancialYearOne(dateMs = timeNow()){
		if (!this.hasFinancialYear()) {
			return false;
		}
		let yearOneStartMs = this.getYearOneStartMs();
		let yearOneEndMs = this.getYearOneEndMs();
		return dateMs >= yearOneStartMs && dateMs < yearOneEndMs;
	}

	isFinancialYearTwo(dateMs = timeNow()){
		if (!this.hasFinancialYear()) {
			return false;
		}
		let yearTwoStartMs = this.getYearTwoStartMs();
		let yearTwoEndMs = this.getYearTwoEndMs();
		return dateMs >= yearTwoStartMs && dateMs < yearTwoEndMs;
	}

	sameFinancialYear(dateMsArray = []) {
		if(!this.hasFinancialYear()) {
			return false;
		}
		let yearOne = true
		let yearTwo = true
		for(let dateMs of dateMsArray) {
			if(!this.isFinancialYearOne(dateMs)){
				yearOne = false;
			}
			if(!this.isFinancialYearTwo(dateMs)){
				yearTwo = false;
			}
		}
		return yearOne || yearTwo;
	}

	isFinancialYearOneOrTwo(dateMs) {
		return this.isFinancialYearOne(dateMs) || this.isFinancialYearTwo(dateMs)
	}

	hasAllocatedFunds(){
		return this.fundsYearOne || this.fundsYearTwo;
	}

	hasFinancialYear() {
		return !!this.financialYearStartMonthDateMs
	}

	getYearOneStartMs() {
		if (!this.hasFinancialYear()) {
			return null;
		}
		let startOfYearMs = dayjs(this.createdAt).startOf('year').valueOf()
		return startOfYearMs + this.financialYearStartMonthDateMs
	}
	getYearOneEndMs() {
		let startOfYearMs = this.getYearOneStartMs();
		if (!startOfYearMs) {
			return null;
		}
		return dayjs(startOfYearMs).add(1, 'year').subtract(1, 'day').endOf('day').valueOf();
	}
	getYearTwoStartMs() {
		if (!this.hasFinancialYear()) {
			return null;
		}
		let startOfYearMs = dayjs(this.createdAt).add(1, "year").startOf('year').valueOf()
		return startOfYearMs + this.financialYearStartMonthDateMs
	}
	getYearTwoEndMs() {
		let startOfYearMs = this.getYearTwoStartMs();
		if (!startOfYearMs) {
			return null;
		}
		return dayjs(startOfYearMs).add(1, 'year').subtract(1, 'day').endOf('day').valueOf();
	}

	fundsFormattedYearOne() {
		return this.fundsFormatted(this.getYearOneStartMs())
	}

	fundsFormattedYearTwo() {
		return this.fundsFormatted(this.getYearTwoStartMs())
	}

	fundsFormatted(dateMs) {
		let title = FundingSourceNameMap[this.type]
		if(!dateMs){
			return title;
		}
		let yearFormatted = ""

		if(this.isFinancialYearOne(dateMs)){
			yearFormatted = this.getFinancialYearOneFormatted()
		} else if(this.isFinancialYearTwo(dateMs)){
			yearFormatted = this.getFinancialYearTwoFormatted()
		}

		if(yearFormatted){
			yearFormatted = ` for ${yearFormatted}`;
		}
		return `${title}${yearFormatted}`
	}

	getFinancialYearOneFormatted() {
		if (!this.hasFinancialYear()) {
			return ""
		}
		let startOfYearOneMs = this.getYearOneStartMs();
		let endOfYearOneMs = this.getYearOneEndMs();

		let format = "MMM YYYY";
		let yearOneStart = dayjs(startOfYearOneMs).format(format)
		let yearOneEnd = dayjs(endOfYearOneMs).format(format);
		return `${yearOneStart}/${yearOneEnd}`;
	}

	getFinancialYearTwoFormatted() {
		if (!this.hasFinancialYear()) {
			return ""
		}
		let startOfYearTwoMs = this.getYearTwoStartMs();
		let endOfYearTwoMs = this.getYearTwoEndMs();

		let format = "MMM YYYY";
		let yearTwoStart = dayjs(startOfYearTwoMs).format(format)
		let yearTwoEnd = dayjs(endOfYearTwoMs).format(format);
		return `${yearTwoStart}/${yearTwoEnd}`;

	}

	fillInvoice(invoiceItem, options = {}) {
		let {clientProfile, userProfile} = options;
		if(clientProfile){
			let fundingSourcesWrapper = clientProfile.getCommissionDetails().getFundingSourcesWrapper()
			invoiceItem.localAuthorityId = fundingSourcesWrapper.localAuthorityId;
		}

		invoiceItem.fundingSourceId = this.id
		invoiceItem.recipientId = this.id

		// invoiceItem.recipientName = this.billingOrganization;
		// invoiceItem.recipientEmail = this.billingEmail;

		invoiceItem.recipientContactDetails.name = this.billingOrganization;
		invoiceItem.recipientContactDetails.email = this.billingEmail;

		//TODO this needs to go into respective subclass. This requires @TypeClass decorator to work properly and time to implement. Maybe next time
		switch (this.type) {
			case FundingSourceEnum.ASF:
				this.fillInvoiceASF(invoiceItem, options);
				break;
			case FundingSourceEnum.LOCAL:
				this.fillInvoiceLocal(invoiceItem, options);
				break;
			case FundingSourceEnum.OTHER:
				this.fillInvoiceOther(invoiceItem, options);
				break;
		}
		return invoiceItem
	}
	fillInvoiceASF(invoiceItem, {clientProfile, userProfile} = {}) {
		invoiceItem.asfNumber = this.asfNumber;
		invoiceItem.asfPurchaseOrderNumber = this.asfPurchaseOrderNumber;
	}

	fillInvoiceLocal(invoiceItem, {clientProfile, userProfile} = {}) {

	}

	fillInvoiceOther(invoiceItem) {

	}

	expand(){
		let res = []
		if(this.hasFinancialYear()){
			let fundingSourceYearOne = classToClass(this)
			fundingSourceYearOne.fundsYearTwo = null

			let fundingSourceYearTwo = classToClass(this)
			fundingSourceYearTwo.fundsYearOne = fundingSourceYearTwo.fundsYearTwo
			fundingSourceYearTwo.fundsYearTwo = null
			fundingSourceYearTwo.createdAt = dayjs(fundingSourceYearTwo.createdAt).add(1, 'year').valueOf()

			fundingSourceYearOne.id = `${this.id}_1`
			fundingSourceYearTwo.id = `${this.id}_2`

			if(fundingSourceYearOne.fundsYearOne){
				res.push(fundingSourceYearOne)
			}
			if(fundingSourceYearTwo.fundsYearOne){
				res.push(fundingSourceYearTwo)
			}
		}
		if(res.length === 0){
			res.push(classToClass(this))
		}
		console.log("FundingSourceItem.expand: res = ", res, ", this = ", this)
		return res
	}


}
